import type { UserEventContext } from '../repositories/storyRepository';
import type { StoryElement } from '../types';
import type { RankedStory } from './feedsV2';

/** The feeds v2 rank a feed or For You serving gave a story (see `utils/feedsV2`). */
export const RANK_KEYS = ['rank_score', 'rank_grade', 'rank_grade_confidence', 'rank_relevance', 'rank_age_hours'] as const;
type RankKey = (typeof RANK_KEYS)[number];

/** A served row: a stored-article row, plus the v2 rank when a v2 build served it. */
export type ServedRow = StoryElement & Partial<Pick<RankedStory, RankKey>>;

/**
 * The feed-specific half of a story's score, as the surface that served it saw
 * it. The admin article page (/article/admin/:id) reads everything intrinsic to
 * the article from the database, but a feed score, a cluster's rank score or a
 * search run's verdicts belong to one serving of one feed and are not stored
 * per article. The page re-reads that serving (see ServedSource), or reads the
 * values the link carried (a search, see servedArticleAdminHref), and shows the
 * same numbers the reader saw next to the stored breakdown.
 */
export interface ServedContext {
  surface: UserEventContext['surface'];
  surface_id?: string | null;
  feed_score?: StoryElement['feed_score'];
  cluster_rank_score?: StoryElement['cluster_rank_score'];
  composite_score?: StoryElement['composite_score'];
  base_score?: StoryElement['base_score'];
  relevance_score?: StoryElement['relevance_score'];
  judgment_justification?: StoryElement['judgment_justification'];
  llm_relevance_judgments?: StoryElement['llm_relevance_judgments'];
  for_you_searches?: StoryElement['for_you_searches'];
  rank_score?: number;
  rank_grade?: number;
  rank_grade_confidence?: number;
  rank_relevance?: number;
  rank_age_hours?: number;
}

/**
 * Which serving a row came from: everything the admin page needs to re-read
 * its served fields, and nothing it can read for itself. A feed is named by
 * id; For You is the viewer's own composition, so it needs no id. A search
 * stores no results, so it cannot be re-read: its links carry the served
 * values instead (servedArticleAdminHref).
 */
export type ServedSource =
  | { surface: 'feed'; id: string }
  | { surface: 'for_you' };

const SERVED_KEYS = [
  'feed_score',
  'cluster_rank_score',
  'composite_score',
  'base_score',
  'relevance_score',
  'judgment_justification',
  'llm_relevance_judgments',
  'for_you_searches',
  ...RANK_KEYS,
] as const;

/** `?from=feed:<feed id>` or `?from=for_you`. */
export const FROM_PARAM = 'from';
/** The served values themselves, as JSON: what a search link carries, since a
 * search stores no results to re-read (and what links built before FROM_PARAM
 * carried for every surface, so those bookmarks keep working). */
export const SERVED_PARAM = 'served';

/** Capture the served fields a row actually carries (absent ones stay absent). */
export function servedContext(story: ServedRow, context: UserEventContext): ServedContext {
  const served: ServedContext = { surface: context.surface };
  if (context.surfaceId) served.surface_id = context.surfaceId;
  for (const key of SERVED_KEYS) {
    if (story[key] != null) (served as unknown as Record<string, unknown>)[key] = story[key];
  }
  return served;
}

/** The served fields of `storyId`'s row in a re-read serving; null when it is
 * not a row there (a rebuild moved it out of the feed, or another member now
 * fronts its cluster). */
export function servedContextFromRows(rows: ServedRow[], storyId: string, source: ServedSource): ServedContext | null {
  const row = rows.find((r) => r.id === storyId);
  if (!row) return null;
  return servedContext(row, { surface: source.surface, surfaceId: source.surface === 'for_you' ? null : source.id });
}

/** The serving a row's surface can be re-read from; null for surfaces whose
 * rows carry nothing beyond the stored article (a permalink, the admin lists)
 * and for a search, whose results are not stored. */
export function servedSource(context: UserEventContext): ServedSource | null {
  if (context.surface === 'for_you') return { surface: 'for_you' };
  if (context.surface === 'feed' && context.surfaceId) return { surface: 'feed', id: context.surfaceId };
  return null;
}

/** Read the `from` query parameter back; null when the link carried none.
 * A malformed value throws, so the page reports it rather than silently
 * dropping the feed-specific rows. */
export function parseServedSource(raw: string | null): ServedSource | null {
  if (raw == null) return null;
  if (raw === 'for_you') return { surface: 'for_you' };
  const [surface, id, ...rest] = raw.split(':');
  if (surface === 'feed' && id && rest.length === 0) return { surface, id };
  throw new Error(`"${raw}" is not feed:<id> or for_you`);
}

/**
 * The admin page link for a served row. A feed or For You serving is named
 * (FROM_PARAM) and re-read there; a search stores no results, so its link
 * carries the row's served values (SERVED_PARAM).
 */
export function servedArticleAdminHref(story: ServedRow, context: UserEventContext): string {
  if (context.surface !== 'search') return articleAdminHref(story.id, servedSource(context));
  const params = new URLSearchParams({ [SERVED_PARAM]: JSON.stringify(servedContext(story, context)) });
  return `/article/admin/${encodeURIComponent(story.id)}?${params}`;
}

export function articleAdminHref(storyId: string, source?: ServedSource | null): string {
  const path = `/article/admin/${encodeURIComponent(storyId)}`;
  if (!source) return path;
  // The colon is legal in a query value; encoding only the id keeps it readable.
  const value = source.surface === 'for_you' ? source.surface : `${source.surface}:${encodeURIComponent(source.id)}`;
  return `${path}?${FROM_PARAM}=${value}`;
}

/** Read the `served` query parameter back; null when the link carried none.
 * A malformed value throws, like parseServedSource. */
export function parseServedContext(raw: string | null): ServedContext | null {
  if (raw == null) return null;
  const value: unknown = JSON.parse(raw);
  if (typeof value !== 'object' || value === null || typeof (value as ServedContext).surface !== 'string') {
    throw new Error('served context is not an object with a surface');
  }
  return value as ServedContext;
}
